import {
  Backdrop,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  styled,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import InputDate from "../../components/InputDate";
import InputTextField from "../../components/InputTextField";
import { WarningModal } from "../../components/WarningModal";
import { AddRiwayatPekerjaanPayload } from "../../models/RiwayatPekerjaan";
import { toIsoStringDate } from "../../utils/dateConvert";
import separateNumber, { formatNumber } from "../../utils/formatPrice";

const SquareGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: "#FFF",
  height: "auto",
  width: "90%",
  maxWidth: "900px",
  boxShadow: "1px 1px 10px #888888",
  borderRadius: "10px",
  padding: "1%",
  [theme.breakpoints.down("sm")]: {
    padding: "10px",
  },
  margin: "1%",
}));

const FullButton = styled(Button)(({ theme }) => ({
  width: "70%",
}));

const ScrollGrid = styled(Grid)(({ theme }) => ({
  overflow: "auto",
  maxHeight: "90vh",
}));

type Props = {
  open: boolean;
  value: AddRiwayatPekerjaanPayload;
  handleClose: () => void;
  handleEdit: (e: any) => void;
};

export default function PekerjaanTerakhirModal({
  value,
  open,
  handleClose,
  handleEdit,
}: Props) {
  const [perusahaan, setPerusahaan] = useState(value.NAMA_PERUSAHAAN);
  const [jenisUsaha, setJenisUsaha] = useState(value.JENIS_USAHA);
  const [jabatanAwal, setJabatanAwal] = useState(value.JABATAN_AWAL);
  const [jabatanAkhir, setJabatanAkhir] = useState(value.JABATAN_AKHIR);
  const [tahunMasuk, setTahunMasuk] = useState(value.TAHUN_MASUK);
  const [tahunKeluar, setTahunKeluar] = useState<string | null>(
    value.TAHUN_KELUAR
  );
  const [jumlahKaryawan, setJumlahKaryawan] = useState(value.JUMLAH_KARYAWAN);
  const [atasanLangsung, setAtasanLangsung] = useState(
    value.NAMA_ATASAN_LANGSUNG
  );
  const [direktur, setDirektur] = useState(value.NAMA_DIREKTUR);
  const [gajiTerakhir, setGajiTerakhir] = useState(value.GAJI_TERAKHIR);
  const [alasanBerhenti, setAlasanBerhenti] = useState(value.ALASAN_BERHENTI);

  const handlePerusahaan = (e: any) => {
    setPerusahaan(e.target.value);
  };
  const handleJenisUsaha = (e: any) => {
    setJenisUsaha(e.target.value);
  };
  const handleJabatanAwal = (e: any) => {
    setJabatanAwal(e.target.value);
  };
  const handleJabatanAkhir = (e: any) => {
    setJabatanAkhir(e.target.value);
  };
  const handleTahunMasuk = (e: any) => {
    setTahunMasuk(e);
  };
  const handleTahunKeluar = (e: any) => {
    setTahunKeluar(e);
  };
  const handleJumlahKaryawan = (e: any) => {
    setJumlahKaryawan(separateNumber(e.target.value));
  };
  const handleAtasanLangsung = (e: any) => {
    setAtasanLangsung(e.target.value);
  };
  const handleDirektur = (e: any) => {
    setDirektur(e.target.value);
  };
  const handleGajiTerakhir = (e: any) => {
    setGajiTerakhir(separateNumber(e.target.value));
  };
  const handleAlasanBerhenti = (e: any) => {
    setAlasanBerhenti(e.target.value);
  };

  const [warningModalState, setWarningModalState] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const handleSave = () => {
    if (
      perusahaan === "" ||
      jenisUsaha === "" ||
      jabatanAwal === "" ||
      jabatanAkhir === "" ||
      atasanLangsung === "" ||
      direktur === "" ||
      alasanBerhenti === ""
    ) {
      setWarningMessage("Mohon Untuk Lengkapi Pekerjaan Terakhir");
      setWarningModalState(true);
      return;
    }
    if (!masihBekerja) {
      const tanggalMasuk = new Date(tahunMasuk);
      const tanggalKeluar = tahunKeluar ? new Date(tahunKeluar) : null;
      if (tanggalKeluar && tanggalKeluar < tanggalMasuk) {
        setWarningMessage("Tanggal Keluar Tidak Boleh Sebelum Tanggal Masuk.");
        setWarningModalState(true);
        return;
      }
    }
    handleEdit({
      NAMA_PERUSAHAAN: perusahaan,
      JENIS_USAHA: jenisUsaha,
      JABATAN_AWAL: jabatanAwal,
      JABATAN_AKHIR: jabatanAkhir,
      TAHUN_MASUK: toIsoStringDate(tahunMasuk),
      TAHUN_KELUAR: masihBekerja
        ? null
        : toIsoStringDate(tahunKeluar as string),
      JUMLAH_KARYAWAN: formatNumber(jumlahKaryawan),
      NAMA_ATASAN_LANGSUNG: atasanLangsung,
      NAMA_DIREKTUR: direktur,
      GAJI_TERAKHIR: noGaji ? null : formatNumber(gajiTerakhir),
      ALASAN_BERHENTI: alasanBerhenti,
    });
    handleClose();
  };

  const [masihBekerja, setMasihBekerja] = useState(false);

  const handleMasihBekerja = () => {
    if (!masihBekerja) {
      setTahunKeluar(toIsoStringDate(Date.now()));
    }
    setMasihBekerja(!masihBekerja);
  };

  const [noGaji, setNoGaji] = useState(false);

  const handleNoGaji = () => {
    if (!noGaji) {
      setGajiTerakhir(0);
    }
    setNoGaji(!noGaji);
  };

  return (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
    >
      <WarningModal
        open={warningModalState}
        handleClose={() => {
          setWarningModalState(false);
        }}
        text={warningMessage}
      />
      <SquareGrid container>
        <ScrollGrid container>
          <Grid container justifyContent="flex-start" paddingLeft={"10.5%"} item xs={6}
            paddingBottom={
              {
                xs: 5,
                sm: 5,
                md: 5,
                lg: 5,
                xl: 5
              }}>
            <Typography variant="subtitle1" color="black">
              Pekerjaan Terakhir
            </Typography>
          </Grid>
          <Grid container justifyContent="flex-end" item xs={6}>
            <Button onClick={handleClose}>Close</Button>
          </Grid>
          <InputTextField
            title={"Nama Perusahaan"}
            value={perusahaan}
            handleEdit={handlePerusahaan}
          />
          <InputTextField
            title={"Jenis Usaha"}
            value={jenisUsaha}
            handleEdit={handleJenisUsaha}
          />
          <InputTextField
            title={"Jabatan Awal"}
            value={jabatanAwal}
            handleEdit={handleJabatanAwal}
          />
          <InputTextField
            title={"Jabatan Akhir"}
            value={jabatanAkhir}
            handleEdit={handleJabatanAkhir}
          />
          <InputDate
            title={"Tanggal Masuk"}
            value={tahunMasuk}
            handleEdit={handleTahunMasuk}
          />
          {masihBekerja ? (
            <Grid container justifyContent="center">
              <Grid container justifyContent="center" sx={{ padding: "1%" }}>
                <Grid item xs={12} sx={{ paddingLeft: "10%" }}>
                  <Typography variant="h6" color="black">
                    Tanggal Keluar
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <InputDate
              title={"Tanggal Keluar"}
              value={tahunKeluar}
              handleEdit={handleTahunKeluar}
            />
          )}
          <Grid container justifyContent="center">
            <Grid
              container
              justifyContent="flex-start"
              sx={{ paddingLeft: "11%" }}
            >
              <FormControlLabel
                label={
                  <Typography color="black">
                    {" "}
                    Masih Bekerja Di Perusahaan
                  </Typography>
                }
                control={
                  <Checkbox
                    checked={masihBekerja}
                    onChange={handleMasihBekerja}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
              />
            </Grid>
          </Grid>

          <InputTextField
            title={"Jumlah Karyawan"}
            value={jumlahKaryawan}
            handleEdit={handleJumlahKaryawan}
          />
          <InputTextField
            title={"Nama Atasan Langsung"}
            value={atasanLangsung}
            handleEdit={handleAtasanLangsung}
          />
          <InputTextField
            title={"Nama Direktur"}
            value={direktur}
            handleEdit={handleDirektur}
          />
          {noGaji ? (
            <Grid container justifyContent="center">
              <Grid container justifyContent="center" sx={{ padding: "1%" }}>
                <Grid item xs={12} sx={{ paddingLeft: "10%" }}>
                  <Typography variant="h6" color="black">
                    Gaji Terakhir
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <InputTextField
              title={"Gaji Terakhir"}
              value={gajiTerakhir}
              handleEdit={handleGajiTerakhir}
              adormentRp
            />
          )}
          <Grid container justifyContent="center">
            <Grid
              container
              justifyContent="flex-start"
              sx={{ paddingLeft: "11%" }}
            >
              <FormControlLabel
                label={
                  <Typography color="black">
                    {" "}
                    Memilih untuk tidak memberitahu gaji.
                  </Typography>
                }
                control={
                  <Checkbox
                    checked={noGaji}
                    onChange={handleNoGaji}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
              />
            </Grid>
          </Grid>
          <InputTextField
            title={"Alasan Berhenti"}
            value={alasanBerhenti}
            handleEdit={handleAlasanBerhenti}
          />
          <Grid container justifyContent="center" item lg={12}
            sx={{ mt: 3 }}
            paddingTop={{
              xs: 2,
              sm: 2,
              md: 2,
              lg: 2,
              xl: 2
            }}
            paddingBottom={{
              xs: 2,
              sm: 2,
              md: 2,
              lg: 2,
              xl: 2
            }}>
            <FullButton variant="contained" onClick={handleSave}>
              Save
            </FullButton>
          </Grid>
        </ScrollGrid>
      </SquareGrid>
    </Backdrop>
  );
}
